import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Paper,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Container,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { motion } from "framer-motion";
import LandscapeIcon from "@mui/icons-material/Landscape";
import TerrainIcon from "@mui/icons-material/Terrain";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WaterIcon from "@mui/icons-material/Water";
import { useNavigate } from "react-router-dom";
import TourCard from "../Components/Cards/TourCard";

const LehLadakhPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const tourPackages = [
    {
      id: "ll1",
      title: "Complete Leh–Ladakh Circuit",
      places: "Leh • Nubra Valley • Pangong • Tso Moriri • Sham Valley",
      price: "₹ 29,999/-",
      image:
        "https://www.lehladakhindia.com/wp-content/uploads/2013/04/pangong-lake-ladakh-1.jpg",
      rating: 4.9,
    },

    {
      id: "ll2",
      title: "Nubra Valley & Pangong Lake Tour",
      places: "Leh • Khardung La • Nubra Valley • Pangong Lake",
      price: "₹ 22,499/-",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQIXrPAclnznOsTdPmeIdBm_m47XQ3R9sqzFw&s",
      rating: 4.8,
    },
    {
      id: "ll3",
      title: "Tso Moriri & Changthang Expedition",
      places: "Leh • Chang La • Tso Moriri • Korzok Village",
      price: "₹ 24,999/-",
      image:
        "https://images.unsplash.com/photo-1605649487212-47bdab064df7?q=80&w=800&auto=format&fit=crop",
      rating: 4.7,
    },
  ];

  const enrichTour = (tour, index) => {
    const durations = ["7D / 6N", "6D / 5N", "7D / 6N", "10D / 9N"];
    const tags = ["Bestseller", "Top Rated", "Explorer Choice", "Extreme Adventure"];

    return {
      ...tour,
      duration: durations[index % durations.length],
      tag: tags[index % tags.length],
      oldPrice:
        "₹ " +
        (parseInt(tour.price.replace(/[₹,/-]/g, "")) + 6000).toLocaleString() +
        "/-",
      discountEndsIn: "Limited Seats Available",
    };
  };

  return (
    <Box sx={{ backgroundColor: "#F9FAFB", minHeight: "100vh" }}>
      
      {/* HERO SECTION */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "60vh", md: "75vh" },
          backgroundImage:
            "url(https://kashmirmountains.com/wp-content/uploads/Nubra-Valley.jpg)", // Landscape suitable for Ladakh
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, textAlign: "center", color: "white" }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                fontSize: { xs: "2.5rem", md: "4.5rem" },
                mb: 2,
                textShadow: "0 4px 12px rgba(0,0,0,0.5)",
              }}
            >
              Leh & Ladakh
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 300, letterSpacing: 2, mb: 4, opacity: 0.9 }}
            >
              THE ROOF OF THE WORLD
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => document.getElementById("packages").scrollIntoView({ behavior: "smooth" })}
              sx={{
                // Changed to Ice Blue to match the cold theme of Ladakh
                backgroundColor: "#0EA5E9", 
                color: "white",
                px: 4,
                py: 1.5,
                borderRadius: 50,
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0 4px 14px rgba(14, 165, 233, 0.4)",
                "&:hover": { backgroundColor: "#0284C7", transform: "translateY(-2px)" },
              }}
            >
              Explore Packages
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* INTRO SECTION */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Typography variant="overline" sx={{ color: "#059669", fontWeight: 700, letterSpacing: 1.5 }}>
            LAND OF HIGH PASSES
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mt: 1,
              mb: 3,
              color: "#1F2937"
            }}
          >
            Feel the Rhythm of the Himalayas
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#6B7280", lineHeight: 1.8, fontSize: "1.1rem" }}
          >
            Ladakh is not a destination you simply visit — it is a land you feel. High-altitude deserts, ancient monasteries, turquoise lakes, and legendary mountain roads define this raw Himalayan region. From Pangong Lake’s surreal colors to Nubra Valley’s sand dunes and the remote landscapes of Zanskar, Ladakh offers one of the most powerful travel experiences in India.
          </Typography>
        </motion.div>
      </Container>

      {/* PACKAGES SECTION */}
      <Box id="packages" sx={{ py: 6, backgroundColor: "#fff" }}>
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 5,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Curated Tour Packages
          </Typography>
          
          <Grid container spacing={4}>
            {tourPackages.map((tour, index) => (
              <Grid item xs={12} sm={6} md={3} key={tour.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <TourCard
                    tour={tour}
                    index={index}
                    onViewDetails={() =>
                      navigate("/tourdetails", {
                        state: enrichTour(tour, index),
                      })
                    }
                    onEnquire={() => navigate("/booking")}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* PLACES SECTION (ZIG-ZAG LAYOUT) */}
      <Box sx={{ py: 10, backgroundColor: "#F9FAFB" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 8,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Places You Will Explore
          </Typography>

          {[
            {
              title: "Leh Town",
              img: "https://images.unsplash.com/photo-1589182373726-e4f658ab50f0?q=80&w=800&auto=format&fit=crop",
              desc: "The cultural heart of Ladakh, home to markets, cafés, Leh Palace, and monasteries.",
            },
            {
              title: "Nubra Valley",
              img: "https://kashmirmountains.com/wp-content/uploads/Nubra-Valley.jpg",
              desc: "Cold desert valley with sand dunes, double-humped camels, and Diskit Monastery.",
            },
            {
              title: "Pangong Lake",
              img: "https://discoverlehladakh.in/wp-content/uploads/2024/04/Pangong-lake-tour-package-from-Leh-Ladakh.jpg",
              desc: "High-altitude lake famous for its changing shades of blue.",
            },
            {
              title: "Zanskar Valley",
              img: "https://altitudeadventureholidays.com/wp-content/uploads/2024/05/Zanskar-Valley-Cycle-Expedition-12.webp",
              desc: "One of the most remote valleys in the Himalayas, known for dramatic landscapes.",
            },
          ].map((place, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Grid
                container
                spacing={6}
                alignItems="center"
                sx={{
                  mb: 10,
                  flexDirection: { xs: "column", md: i % 2 === 0 ? "row" : "row-reverse" },
                }}
              >
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      height: { xs: "300px", md: "400px" },
                    }}
                  >
                    <CardMedia component="img" image={place.img} alt={place.title} sx={{ height: "100%", objectFit: "cover" }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="overline"
                    sx={{ color: "#059669", fontWeight: "bold" }}
                  >
                    DESTINATION
                  </Typography>
                  <Typography variant="h3" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, my: 2 }}>
                    {place.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#4B5563", lineHeight: 1.8 }}>
                    {place.desc}
                  </Typography>
                </Grid>
              </Grid>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* EXPERIENCES SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Experiences That Define Ladakh
          </Typography>
          <Typography sx={{ textAlign: "center", color: "#6B7280", mb: 6 }}>
            Discover the raw beauty of the cold desert.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <TerrainIcon />, text: "High-altitude road trips & passes" },
              { icon: <LandscapeIcon />, text: "Surreal Himalayan landscapes" },
              { icon: <TempleHinduIcon />, text: "Ancient monasteries & culture" },
              { icon: <WaterIcon />, text: "High-altitude lakes & rivers" },
              { icon: <AcUnitIcon />, text: "Extreme weather & cold desert life" },
              { icon: <DirectionsCarIcon />, text: "World’s highest motorable roads" },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: "100%",
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "0.3s",
                      "&:hover": {
                        borderColor: "#0EA5E9", // Changed to Ice Blue hover for Ladakh theme
                        boxShadow: "0 10px 30px -10px rgba(14, 165, 233, 0.15)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 40,
                        color: "#0EA5E9", // Changed icon color
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 60,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography fontWeight={600} sx={{ color: "#374151" }}>
                      {item.text}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TABLE SECTION */}
      <Box sx={{ py: 10, backgroundColor: "#F3F4F6" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ p: 4, borderBottom: "1px solid #E5E7EB" }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
                Ladakh – Travel Overview
              </Typography>
            </Box>
            <Table>
              <TableHead sx={{ backgroundColor: "#064E3B" }}>
                <TableRow>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Place</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Altitude</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Region</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Known For</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["Leh", "3,500 m", "Leh", "Capital & monasteries"],
                  ["Nubra Valley", "3,100 m", "Leh", "Sand dunes & camels"],
                  ["Pangong Lake", "4,225 m", "Changthang", "Blue lake"],
                  ["Tso Moriri", "4,522 m", "Changthang", "Remote lake"],
                  ["Zanskar", "3,700 m", "Zanskar", "Extreme landscapes"],
                ].map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 }, "&:hover": { backgroundColor: "#F9FAFB" } }}
                  >
                    {row.map((cell, j) => (
                      <TableCell key={j} sx={{ color: "#4B5563" }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Container>
      </Box>

      {/* CTA SECTION */}
      <Box
        sx={{
          py: 12,
          backgroundImage: "linear-gradient(135deg, #065F46 0%, #047857 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: "2rem", md: "3rem" }
              }}
            >
              Ready to Explore Ladakh?
            </Typography>
            <Typography sx={{ maxWidth: "800px", mx: "auto", lineHeight: 1.8, fontSize: "1.1rem", opacity: 0.9, mb: 5 }}>
              Ladakh is not easy — and that’s what makes it unforgettable. Travel with experts who understand altitude, terrain, and safety.
            </Typography>
            <Button
              size="large"
              variant="contained"
              sx={{
                backgroundColor: "#D97706",
                color: "white",
                fontWeight: 700,
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                borderRadius: 50,
                boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: "#B45309" },
              }}
              onClick={() => navigate("/booking")}
            >
              Enquire Now
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default LehLadakhPage;